/**
 * The Availability report's view state. ALL of it lives in the URL search params
 * so a view is shareable; "Save view" stores the same string as this browser's
 * default for a bare visit.
 *
 * The URL only carries what differs from the defaults (so a default view is a
 * bare `/dashboard/availability`), and a preset is stored by NAME, never as
 * dates, so "Next 90 days" keeps rolling forward on its own. Only a Custom
 * window stores months.
 */

import type { GroupBy, RowSort } from "@/lib/availability/groups";
import { isMonthStart } from "@/lib/availability/months";
import {
  type ResolvedWindow,
  resolveWindow,
  validateCustomWindow,
  type WindowPreset,
} from "@/lib/availability/window";

export const WINDOW_PRESETS: ReadonlyArray<{ value: WindowPreset; label: string }> = [
  { value: "next90", label: "Next 90 days" },
  { value: "thisQuarter", label: "This quarter" },
  { value: "nextQuarter", label: "Next quarter" },
  { value: "next6", label: "Next 6 months" },
  { value: "rolling12", label: "Rolling 12 months" },
  { value: "fiscalYear", label: "Fiscal year" },
  { value: "ytd", label: "Year to date" },
  { value: "custom", label: "Custom" },
];

export const GROUP_OPTIONS: ReadonlyArray<{ value: GroupBy; label: string }> = [
  { value: "homeRole", label: "Home Role" },
  { value: "discipline", label: "Discipline" },
  { value: "account", label: "Account" },
  { value: "manager", label: "Manager" },
  { value: "none", label: "No grouping" },
];

export const SORT_OPTIONS: ReadonlyArray<{ value: RowSort; label: string }> = [
  { value: "mostAvailable", label: "Most available first" },
  { value: "mostOver", label: "Most over first" },
  { value: "name", label: "Name" },
];

/** The four tiles. Each is a row filter; "dataGaps" is no Home Role OR an open issue. */
export type ReportTile = "bookableFree" | "overUtilized" | "rollingOff" | "dataGaps";

export const REPORT_TILES: readonly ReportTile[] = [
  "bookableFree",
  "overUtilized",
  "rollingOff",
  "dataGaps",
];

export type Density = "comfortable" | "compact";

/** A clicked month: one person's, or one group's. Group keys are `null` for Unassigned. */
export type Selection =
  | { kind: "resource"; resourceId: string; month: string }
  | { kind: "group"; groupKey: string | null; month: string };

/** What the URL carries. Every key is optional and absent means the default. */
export interface AvailabilitySearch {
  /** Window preset; absent = "next90". */
  w?: WindowPreset;
  /** Custom window months (`YYYY-MM-01`), only with `w=custom`. */
  from?: string;
  to?: string;
  /** Resource search. */
  q?: string;
  /** Group by; absent = Home Role. */
  g?: GroupBy;
  /** Sort; absent = most available first. */
  s?: RowSort;
  /** Exceptions only (default off). */
  exc?: true;
  /** `false` = leave Proposed Guidance out of Booked (default on). */
  prop?: false;
  /** `false` = include non-delivery Home Roles (default on). */
  deliv?: false;
  /** Show actual months (default off). */
  act?: true;
  /** The active tile filter. */
  tile?: ReportTile;
  /** Compact rows (default comfortable). */
  dens?: "compact";
  /** The open panel, encoded; see {@link encodeSelection}. */
  sel?: string;
}

/** The fully resolved view: every field has a value. */
export interface AvailabilityView {
  preset: WindowPreset;
  /** Only meaningful for `preset === "custom"`. */
  from: string | null;
  to: string | null;
  q: string;
  groupBy: GroupBy;
  sort: RowSort;
  exceptionsOnly: boolean;
  includeProposed: boolean;
  deliveryOnly: boolean;
  showActual: boolean;
  tile: ReportTile | null;
  density: Density;
  selection: Selection | null;
}

export const DEFAULT_PRESET: WindowPreset = "next90";

function oneOf<T extends string>(options: readonly T[], value: unknown): T | undefined {
  return typeof value === "string" ? options.find((option) => option === value) : undefined;
}

/** `true` / `"true"` / `"1"` → true; `false` / `"false"` / `"0"` → false; else undefined. */
function flag(value: unknown): boolean | undefined {
  if (value === true || value === "true" || value === "1" || value === 1) return true;
  if (value === false || value === "false" || value === "0" || value === 0) return false;
  return undefined;
}

function month(value: unknown): string | undefined {
  return typeof value === "string" && isMonthStart(value) ? value : undefined;
}

/**
 * Validate raw search params from the router. Unknown or malformed values are
 * dropped (never thrown), so a hand-edited link degrades to the default.
 */
export function parseAvailabilitySearch(search: Record<string, unknown>): AvailabilitySearch {
  const out: AvailabilitySearch = {};
  const preset = oneOf(
    WINDOW_PRESETS.map((p) => p.value),
    search.w,
  );
  if (preset !== undefined && preset !== DEFAULT_PRESET) out.w = preset;
  if (preset === "custom") {
    const from = month(search.from);
    const to = month(search.to);
    if (from !== undefined) out.from = from;
    if (to !== undefined) out.to = to;
  }
  const q =
    typeof search.q === "string"
      ? search.q.trim()
      : typeof search.q === "number"
        ? String(search.q)
        : "";
  if (q !== "") out.q = q;
  const g = oneOf(
    GROUP_OPTIONS.map((o) => o.value),
    search.g,
  );
  if (g !== undefined && g !== "homeRole") out.g = g;
  const s = oneOf(
    SORT_OPTIONS.map((o) => o.value),
    search.s,
  );
  if (s !== undefined && s !== "mostAvailable") out.s = s;
  if (flag(search.exc) === true) out.exc = true;
  if (flag(search.prop) === false) out.prop = false;
  if (flag(search.deliv) === false) out.deliv = false;
  if (flag(search.act) === true) out.act = true;
  const tile = oneOf(REPORT_TILES, search.tile);
  if (tile !== undefined) out.tile = tile;
  if (search.dens === "compact") out.dens = "compact";
  if (typeof search.sel === "string" && decodeSelection(search.sel) !== null) out.sel = search.sel;
  return out;
}

export function resolveView(search: AvailabilitySearch): AvailabilityView {
  return {
    preset: search.w ?? DEFAULT_PRESET,
    from: search.from ?? null,
    to: search.to ?? null,
    q: search.q ?? "",
    groupBy: search.g ?? "homeRole",
    sort: search.s ?? "mostAvailable",
    exceptionsOnly: search.exc === true,
    includeProposed: search.prop !== false,
    deliveryOnly: search.deliv !== false,
    showActual: search.act === true,
    tile: search.tile ?? null,
    density: search.dens === "compact" ? "compact" : "comfortable",
    selection: search.sel === undefined ? null : decodeSelection(search.sel),
  };
}

/** The minimal search for a view: only non-default fields. Inverse of {@link resolveView}. */
export function viewToSearch(view: AvailabilityView): AvailabilitySearch {
  const out: AvailabilitySearch = {};
  if (view.preset !== DEFAULT_PRESET) out.w = view.preset;
  if (view.preset === "custom") {
    if (view.from !== null) out.from = view.from;
    if (view.to !== null) out.to = view.to;
  }
  if (view.q.trim() !== "") out.q = view.q.trim();
  if (view.groupBy !== "homeRole") out.g = view.groupBy;
  if (view.sort !== "mostAvailable") out.s = view.sort;
  if (view.exceptionsOnly) out.exc = true;
  if (!view.includeProposed) out.prop = false;
  if (!view.deliveryOnly) out.deliv = false;
  if (view.showActual) out.act = true;
  if (view.tile !== null) out.tile = view.tile;
  if (view.density === "compact") out.dens = "compact";
  if (view.selection !== null) out.sel = encodeSelection(view.selection);
  return out;
}

/** `r|<resourceId>|<month>` for a Resource, `g|<groupKey>|<month>` for a group ("" = Unassigned). */
export function encodeSelection(selection: Selection): string {
  return selection.kind === "resource"
    ? `r|${selection.resourceId}|${selection.month}`
    : `g|${selection.groupKey ?? ""}|${selection.month}`;
}

export function decodeSelection(value: string): Selection | null {
  const parts = value.split("|");
  const kind = parts[0];
  const monthPart = parts[parts.length - 1];
  if (parts.length < 3 || monthPart === undefined || !isMonthStart(monthPart)) return null;
  const id = parts.slice(1, -1).join("|");
  if (kind === "r" && id !== "") return { kind: "resource", resourceId: id, month: monthPart };
  if (kind === "g") return { kind: "group", groupKey: id === "" ? null : id, month: monthPart };
  return null;
}

/**
 * Parse a raw `?a=b&c=%22d%22` string the way the router does (each value is
 * JSON-decoded when it can be), then validate. Used for the saved default view.
 */
export function parseSearchString(search: string): AvailabilitySearch {
  const raw: Record<string, unknown> = {};
  for (const [key, value] of new URLSearchParams(search)) {
    try {
      const decoded: unknown = JSON.parse(value);
      raw[key] = decoded;
    } catch {
      raw[key] = value;
    }
  }
  return parseAvailabilitySearch(raw);
}

/** The window a view shows, plus why a Custom range was refused (it falls back to the default). */
export function windowForView(
  view: AvailabilityView,
  today: string,
): { window: ResolvedWindow; problem: string | null } {
  if (view.preset === "custom") {
    const custom =
      view.from !== null && view.to !== null ? { from: view.from, to: view.to } : undefined;
    const problem = validateCustomWindow(custom);
    if (problem === null && custom !== undefined) {
      return { window: resolveWindow("custom", today, custom), problem: null };
    }
    return { window: resolveWindow(DEFAULT_PRESET, today), problem };
  }
  return { window: resolveWindow(view.preset, today), problem: null };
}

const SAVED_VIEW_KEY = "nos.availability.view";

/** This browser's saved default view (a raw search string), or null. */
export function readSavedView(): string | null {
  try {
    const raw = localStorage.getItem(SAVED_VIEW_KEY);
    return raw !== null && raw !== "" ? raw : null;
  } catch {
    return null;
  }
}

/** Save the current search string as this browser's default. False when storage is unavailable. */
export function writeSavedView(search: string): boolean {
  try {
    localStorage.setItem(SAVED_VIEW_KEY, search);
    return true;
  } catch {
    return false;
  }
}

/** `"YYYY-MM-DD"` for the viewer's local date (the window resolves before data arrives). */
export function localToday(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
